import { ProvenancePanel } from "../components/ProvenancePanel";
import { useEffect, useMemo, useState } from "react";
import type {
  TimelineCommit,
  TimelineEntry,
  TimelineHistoryKind,
  TimelineOption,
  TimelineProjectChangeKind,
} from "@insights/contracts";
import { ActionIcon, Badge, Button, Card, Combobox, Toast } from "@insights/ui";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  ClipboardCopy,
  GitCommitHorizontal,
  RefreshCw,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router";
import { EmptyState, Loading, PageHeader } from "../../../shared/components";
import { AppShell } from "../../../shared/layout";
import {
  useEnvironmentTimeline,
  useTimelineOptions,
} from "../hooks/useEnvironmentTimeline";
import { useBuildConfiguration } from "../hooks/useBuildConfiguration";
import { useCopyReleaseNotes, type ReleaseNotesCopyStatus } from "../hooks/useCopyReleaseNotes";
import "../styles/environment-timeline.css";

const historyLabels: Record<TimelineHistoryKind, string> = {
  initial: "Initial deployment",
  forward: "Forward change",
  rollback: "Rollback",
  divergent: "Divergent history",
};

const projectLabels: Record<TimelineProjectChangeKind, string> = {
  added: "Added",
  changed: "Changed",
  removed: "Removed",
};

function optionValue(option: TimelineOption) {
  return JSON.stringify([option.repository, option.profile]);
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function shortHash(value: string | null) {
  return value?.slice(0, 8) ?? "—";
}

function githubCommitUrl(repository: string, sha: string) {
  return /^[^/\s]+\/[^/\s]+$/.test(repository)
    ? `https://github.com/${repository}/commit/${sha}`
    : null;
}

function CommitList({
  commits,
  direction,
  repository,
}: {
  commits: TimelineCommit[];
  direction: "introduced" | "removed";
  repository: string;
}) {
  if (!commits.length) {
    return null;
  }

  const Icon = direction === "introduced" ? ArrowUp : ArrowDown;

  return (
    <section className="timeline-commit-group">
      <h4>
        <Icon aria-hidden="true" />
        {direction === "introduced" ? "Introduced commits" : "Removed commits"}
      </h4>
      <ul>
        {commits.map((commit) => (
          <li key={`${direction}-${commit.sha}`}>
            {githubCommitUrl(repository, commit.sha) ? (
              <a
                href={githubCommitUrl(repository, commit.sha) ?? undefined}
                rel="noreferrer"
                target="_blank"
              >
                <code>{commit.sha.slice(0, 8)}</code>
                <strong>{commit.title || "Untitled commit"}</strong>
              </a>
            ) : (
              <div className="timeline-commit-summary">
                <code>{commit.sha.slice(0, 8)}</code>
                <strong>{commit.title || "Untitled commit"}</strong>
              </div>
            )}
            <span>
              {commit.author} · {formatDate(commit.timestamp)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TimelineEntryCard({
  canCopyReleaseNotes,
  copyStatus,
  entry,
  onCopyReleaseNotes,
  repository,
  workspaceId,
}: {
  canCopyReleaseNotes: boolean;
  copyStatus: ReleaseNotesCopyStatus | undefined;
  entry: TimelineEntry;
  onCopyReleaseNotes: (entry: TimelineEntry) => void;
  repository: string;
  workspaceId: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <article className="timeline-entry">
      <div className="timeline-marker">
        <GitCommitHorizontal aria-hidden="true" />
      </div>
      <Card className="timeline-card">
        <div className="timeline-card-heading">
          <div>
            <Badge
              tone={
                entry.historyKind === "rollback"
                  ? "warning"
                  : entry.historyKind === "divergent"
                    ? "danger"
                    : "success"
              }
            >
              {historyLabels[entry.historyKind]}
            </Badge>
            <h2>{entry.branchOrTag}</h2>
            <p>
              {formatDate(entry.deployedAt)}
              {entry.actorName ? ` · ${entry.actorName}` : ""}
              {entry.targets.length ? ` · ${entry.targets.join(", ")}` : ""}
            </p>
            <div className="timeline-head">
              <code>{entry.head.sha.slice(0, 8)}</code>
              <span>{entry.head.title || "Untitled commit"}</span>
            </div>
          </div>
          <div className="timeline-card-actions">
            <ActionIcon
              color={copyStatus === "copied" ? "accent" : "default"}
              disabled={!canCopyReleaseNotes || copyStatus === "generating"}
              aria-busy={copyStatus === "generating"}
              icon={copyStatus === "copied" ? <Check /> : copyStatus === "generating" ? <RefreshCw /> : <ClipboardCopy />}
              label={
                copyStatus === "generating"
                  ? "Generating release notes…"
                  : copyStatus === "ready"
                    ? "Copy ready"
                    : "Copy release notes"
              }
              onClick={() => onCopyReleaseNotes(entry)}
              size="small"
            />
            {entry.builds.map((build) => (
              <Button
                color="neutral"
                key={build.buildId}
                onClick={() =>
                  navigate(`/w/${workspaceId}/builds/${build.buildId}`, {
                    state: {
                      buildModalBackground: location,
                      buildsReturnTo: `${location.pathname}${location.search}`,
                    },
                  })
                }
                size="small"
              >
                {entry.builds.length === 1
                  ? "View build"
                  : `View ${build.targets.join(", ") || "build"}`}
              </Button>
            ))}
          </div>
        </div>

        {!entry.historyComplete && (
          <div className="timeline-warning" role="status">
            <AlertTriangle aria-hidden="true" />
            Commit contribution is incomplete because the available history does
            not connect these deployments.
          </div>
        )}

        <ProvenancePanel workspaceId={workspaceId} buildId={entry.buildId} state />
        <div className="timeline-projects">
          {entry.projects.map((project) => (
            <div className="timeline-project" key={project.projectId}>
              <Badge
                tone={
                  project.change === "removed"
                    ? "danger"
                    : project.change === "added"
                      ? "success"
                      : "neutral"
                }
              >
                {projectLabels[project.change]}
              </Badge>
              <strong>{project.projectName}</strong>
              <code>
                {shortHash(project.previousHash)} → {shortHash(project.currentHash)}
              </code>
            </div>
          ))}
        </div>

        <div className="timeline-commits">
          <CommitList
            commits={entry.introducedCommits}
            direction="introduced"
            repository={repository}
          />
          <CommitList
            commits={entry.removedCommits}
            direction="removed"
            repository={repository}
          />
        </div>
      </Card>
    </article>
  );
}

export function EnvironmentTimelinePage() {
  const { workspaceId } = useParams();
  const options = useTimelineOptions(workspaceId);
  const configuration = useBuildConfiguration(workspaceId).query;
  const [selectedValue, setSelectedValue] = useState<string>();
  const clipboard = useCopyReleaseNotes(
    workspaceId,
    JSON.stringify(configuration.data?.configuration),
  );
  const comboboxOptions = useMemo(
    () =>
      (options.data ?? []).map((option) => ({
        value: optionValue(option),
        label: `${option.repository} · ${option.profile}`,
      })),
    [options.data],
  );

  useEffect(() => {
    if (
      options.data?.[0] &&
      !options.data.some((option) => optionValue(option) === selectedValue)
    ) {
      setSelectedValue(optionValue(options.data[0]));
    }
  }, [options.data, selectedValue]);

  const selectedOption = options.data?.find(
    (option) => optionValue(option) === selectedValue,
  );
  const timeline = useEnvironmentTimeline(workspaceId, selectedOption);
  const entries = timeline.data?.pages.flatMap((page) => page.items) ?? [];

  function exportSelection(entry: TimelineEntry) {
    return {
      context: "timeline" as const,
      repository: selectedOption!.repository,
      profile: selectedOption!.profile,
      buildId: entry.buildId,
    };
  }

  return (
    <AppShell>
      <PageHeader eyebrow="Delivery stream" title="Environment Timeline" />
      {clipboard.message && (
        <Toast onDismiss={clipboard.dismiss} title="Release notes" tone="default">
          {clipboard.message}
        </Toast>
      )}

      {options.isLoading && <Loading />}
      {options.isError && (
        <EmptyState
          title="Timeline could not be loaded"
          text="Check your access or retry the request."
        />
      )}
      {options.data?.length === 0 && (
        <EmptyState
          title="No timeline profiles"
          text="Configure a release profile for an environment with successful deployments."
        />
      )}

      {options.data && options.data.length > 0 && (
        <>
          <div className="timeline-selector">
            <Combobox
              label="Release profile"
              onValueChange={setSelectedValue}
              options={comboboxOptions}
              placeholder="Select a release profile"
              {...(selectedValue ? { value: selectedValue } : {})}
            />
            <ActionIcon
              disabled={options.isFetching || timeline.isFetching}
              icon={
                <RefreshCw
                  className={timeline.isFetching ? "refreshing" : undefined}
                />
              }
              label="Refresh timeline"
              onClick={() => {
                void options.refetch();
                void timeline.refetch();
              }}
            />
          </div>

          {timeline.isLoading && <Loading />}
          {timeline.isError && (
            <EmptyState
              title="Timeline could not be loaded"
              text="Refresh to try loading this release profile again."
            />
          )}
          {!timeline.isLoading && !timeline.isError && entries.length === 0 && (
            <EmptyState
              title="No changes yet"
              text="Successful deployments with an unchanged project state stay hidden."
            />
          )}
          {entries.length > 0 && selectedOption && (
            <div className="timeline-list">
              {entries.map((entry) => (
                <TimelineEntryCard
                  canCopyReleaseNotes={Boolean(configuration.data?.configuration) && !clipboard.isGenerating}
                  copyStatus={clipboard.status(exportSelection(entry))}
                  entry={entry}
                  key={entry.buildId}
                  onCopyReleaseNotes={(selectedEntry) => {
                    void clipboard.copy(exportSelection(selectedEntry));
                  }}
                  repository={selectedOption.repository}
                  workspaceId={workspaceId ?? ""}
                />
              ))}
            </div>
          )}
          {timeline.hasNextPage && (
            <div className="timeline-load-more">
              <Button
                color="neutral"
                disabled={timeline.isFetchingNextPage}
                onClick={() => void timeline.fetchNextPage()}
              >
                {timeline.isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}
    </AppShell>
  );
}
